/**
 * The front picture in the world's tone at the hour (lane TITLE-TONE): the title, the slots and the loading scene take
 * the same grade, light and shadow as play, from the same code (no CSS filter):
 *   - the hour is the game clock (main.ts: the world's day follows the real clock, DAY_SECONDS per day), so the
 *     picture behind the front shows the hour the world will open at;
 *   - the washes always follow the hour as in play (passes/dayGrade.ts `paintDayGrade`: warm wash, cool shade, cloud
 *     shadows, shafts from the upper right, vignette, low mist; passes/hourWash.ts `paintHourWash`: amber dusk, blue
 *     night);
 *   - the *lighting* (lighting.ts drawNight: the dusk multiply, the dark night layer with pools of light) adds only
 *     what the scene's own painting lacks: each scene is painted at a time of day (`PAINTED`), so a moonlit scene at
 *     night is not darkened twice, while a daylight scene at night goes dark with a light pool and a warm lantern
 *     spill round the two heroes, as the player's pool and a carried lantern do in play;
 *   - figures cast the hour's shadow toward the lower left (lighting.ts drawFigureShadows), contact shadow included.
 * Follows the "แสงและเงา" and day-grade settings like play. Cost: a few composites on the small art-pixel canvas.
 */
import { DAY_SECONDS } from '@nine/shared';
import { settings } from '../settings';
import '../render/dayGrade.setting';
import { drawFigureShadows, lightingOn, nightAt, pool } from '../render/lighting';
import { LEVELS, dayShare, paintDayGrade } from '../render/passes/dayGrade';
import { paintHourWash } from '../render/passes/hourWash';
import type { SpriteSet } from '../render/sprites';
import type { SceneId } from './scenes';

/** The time of day each scene is painted at (its sky, its light): what the hour's lighting need not add. */
export type Painted = 'day' | 'dusk' | 'night';
export const PAINTED: Record<SceneId, Painted> = {
  arena: 'day',
  bamboo: 'day',
  blossom: 'day',
  falls: 'day',
  grove: 'day',
  hall: 'day',
  pier: 'day',
  snow: 'day',
  snowfield: 'day',
  village: 'day',
  capital: 'dusk',
  fort: 'dusk',
  island: 'dusk',
  lake: 'dusk',
  mist: 'dusk',
  peak: 'dusk',
  temple: 'dusk',
  camp: 'night',
  fireflies: 'night',
  lantern: 'night',
  moon: 'night',
  stele: 'night',
  storm: 'night',
  valley: 'night',
};

/** Pinned hour for captures and dev (`?hour=22`, or `__frontHour = 22` in the console); null = the game clock. */
let pinned: number | null = (() => {
  const q = new URLSearchParams(location.search).get('hour');
  const h = q === null ? NaN : Number(q);
  return Number.isFinite(h) ? ((h % 24) + 24) % 24 : null;
})();
Object.defineProperty(window, '__frontHour', {
  get: () => pinned,
  set: (v: unknown) => {
    pinned = typeof v === 'number' && Number.isFinite(v) ? ((v % 24) + 24) % 24 : null;
  },
  configurable: true,
});

/** The world's hour now (main.ts makes the world with the same). */
export function frontHour(): number {
  if (pinned !== null) return pinned;
  return ((Date.now() / 1000) % DAY_SECONDS) / (DAY_SECONDS / 24);
}

/** The hour's band, for picking a scene painted at about that time and for the UI's contrast (`data-hour`). */
export function bandAt(hour: number): Painted {
  const { dark, warm } = nightAt(hour);
  return dark > 0.5 ? 'night' : warm > 0.3 || dark > 0.05 ? 'dusk' : 'day';
}

/** The tone is on when play's lights are (the setting). */
export const toneOn = (): boolean => lightingOn();

/** A figure drawn this frame on the main canvas (art px): feet at (x, y), `h` tall; `hero` = one of the duo. */
export interface FigMark {
  x: number;
  y: number;
  h: number;
  hero: boolean;
}

/** The figure's shadows for the hour (cast toward the lower left by day, contact always), on the art grid. */
export function figureShadows(
  g: CanvasRenderingContext2D,
  set: SpriteSet,
  facing: number,
  x: number,
  y: number,
  scene: SceneId | null,
): void {
  // A scene painted at night has no sun to cast with, whatever the clock says.
  const hour = scene && PAINTED[scene] === 'night' ? null : frontHour();
  drawFigureShadows(g, set, facing, Math.round(x), Math.round(y), 1, hour, 0, 0, 1, 0);
}

let nightC: HTMLCanvasElement | null = null;

/**
 * The hour over the finished scene picture (`W` x `H` art px, drawn through the stage's ×R transform), `t` seconds
 * of scene time, `ox`/`oy` the view's anchor (the cloud shadows lie with it), `figs` the figures drawn this frame.
 */
export function paintHour(
  g: CanvasRenderingContext2D,
  W: number,
  H: number,
  scene: SceneId | null,
  t: number,
  ox: number,
  oy: number,
  figs: readonly FigMark[],
): void {
  if (!toneOn()) return;
  const hour = frontHour();
  const { dark, warm } = nightAt(hour);
  const painted = scene ? PAINTED[scene] : 'day';
  // Lighting: only what the painting lacks.
  const warmAdd = painted === 'day' ? warm : 0;
  const darkAdd = painted === 'night' ? 0 : dark;
  if (warmAdd > 0.01) {
    g.save();
    g.globalCompositeOperation = 'multiply';
    g.fillStyle = `rgba(255,${Math.round(205 - 25 * warmAdd)},${Math.round(175 - 35 * warmAdd)},${0.55 * warmAdd})`;
    g.fillRect(0, 0, W, H);
    g.restore();
  }
  if (darkAdd > 0.01) night(g, W, H, darkAdd, figs, ox);
  // The grade: as in play at this hour.
  const lv = LEVELS[settings.dayGradeLevel];
  if (!lv) return;
  paintHourWash(g, W, H, lv, hour);
  const day = dayShare(dark, warm);
  if (day >= 0.02) paintDayGrade(g, W, H, lv, day, t, ox, oy);
}

/** lighting.ts drawNight's dark layer and pools, round the heroes (the player's pool and a carried lantern's spill). */
function night(g: CanvasRenderingContext2D, W: number, H: number, dark: number, figs: readonly FigMark[], ox: number): void {
  nightC ??= document.createElement('canvas');
  if (nightC.width !== W || nightC.height !== H) {
    nightC.width = W;
    nightC.height = H;
  }
  const n = nightC.getContext('2d')!;
  n.globalCompositeOperation = 'copy';
  n.fillStyle = `rgba(14,18,46,${0.6 * dark})`;
  n.fillRect(0, 0, W, H);
  // The duo, else the two figures nearest the subject.
  let lit = figs.filter((f) => f.hero);
  if (!lit.length) lit = [...figs].sort((a, b) => Math.abs(a.x - ox) - Math.abs(b.x - ox)).slice(0, 2);
  const pools = lit.map((f) => ({ x: f.x, y: Math.round(f.y - f.h * 0.55), r: Math.max(8, Math.round(f.h * 0.8)) }));
  n.globalCompositeOperation = 'destination-out';
  for (const p of pools) {
    const img = pool(p.r, 0.9, '0,0,0', 0.92, 0.75);
    n.drawImage(img, p.x - p.r, p.y - img.height / 2);
  }
  n.globalCompositeOperation = 'source-over';
  g.drawImage(nightC, 0, 0);
  // A lantern's warm spill between the two (lighting.ts: a carried lantern, '255,150,64' at 0.16).
  if (pools.length) {
    const cx = Math.round(pools.reduce((s, p) => s + p.x, 0) / pools.length);
    const fy = Math.round(lit.reduce((s, f) => s + f.y - f.h * 0.4, 0) / lit.length);
    const r = Math.max(10, Math.round(Math.max(...lit.map((f) => f.h)) * 0.75));
    g.save();
    g.globalCompositeOperation = 'lighter';
    g.globalAlpha = dark;
    const img = pool(r, 0.7, '255,150,64', 0.16);
    g.drawImage(img, cx - r, fy - img.height / 2);
    g.restore();
  }
}
